/**
 * Workflow Service
 * 
 * Manages the creation and execution of workflow tasks with seamless AI integration.
 * AI agents are triggered automatically based on events, and their results are
 * stored as tasks for human review when needed.
 */

import { serenityClient } from './serenityClient';
import { AGENT_CODES } from '../config/environment';
import type { WorkflowTask, TaskType, TaskPriority, TaskStatus } from '../types';

// Workflow trigger configuration
const WORKFLOW_TRIGGERS = {
  document_uploaded: {
    agentCode: AGENT_CODES.ANALIZADOR,
    taskType: 'document_review' as TaskType,
    confidenceThreshold: 0.7,
    requiresHumanReview: true,
    title: 'Document Analysis',
  },
  case_created: {
    agentCode: AGENT_CODES.SEGMENTADOR,
    taskType: 'segmentation' as TaskType,
    confidenceThreshold: 0.8,
    requiresHumanReview: false,
    title: 'Debtor Segmentation',
  },
  hardship_submitted: {
    agentCode: AGENT_CODES.EVALUADOR,
    taskType: 'hardship_evaluation' as TaskType,
    confidenceThreshold: 0.85,
    requiresHumanReview: true,
    title: 'Hardship Evaluation',
  },
  payment_plan_created: {
    agentCode: AGENT_CODES.VALIDADOR,
    taskType: 'payment_plan_validation' as TaskType,
    confidenceThreshold: 0.9,
    requiresHumanReview: true,
    title: 'Payment Plan Validation',
  },
  escalation_requested: {
    agentCode: AGENT_CODES.JUDICIAL,
    taskType: 'legal_assessment' as TaskType,
    confidenceThreshold: 0.75,
    requiresHumanReview: true,
    title: 'Legal Assessment',
  },
};

interface CreateTaskInput {
  caseId: string;
  debtorId: string;
  entityType: string;
  entityId: string;
  taskType: TaskType;
  title: string;
  description: string;
  priority?: TaskPriority;
  assignedTo?: string;
  assignedRole?: string;
  dueDate?: string;
  parentTaskId?: string;
  aiAgentCode?: string;
}

interface AIExecutionResult {
  success: boolean;
  recommendation?: string;
  confidence?: number;
  rawResult?: unknown;
  error?: string;
}

class WorkflowService {
  /**
   * Creates a new workflow task
   */
  async createTask(input: CreateTaskInput): Promise<WorkflowTask> {
    const taskId = `TASK-${Date.now()}`;
    const now = new Date().toISOString();
    
    const task: Partial<WorkflowTask> = {
      task_id: taskId,
      workflow_type: this.getWorkflowType(input.taskType),
      entity_type: input.entityType,
      entity_id: input.entityId,
      case_id: input.caseId,
      debtor_id: input.debtorId,
      task_type: input.taskType,
      title: input.title,
      description: input.description,
      status: 'pending',
      priority: input.priority || 'normal',
      assigned_to: input.assignedTo,
      assigned_role: input.assignedRole || this.getDefaultRole(input.taskType),
      created_by: 'system',
      ai_agent_code: input.aiAgentCode,
      due_date: input.dueDate || this.calculateDueDate(input.priority || 'normal'),
      requires_approval: this.requiresApproval(input.taskType),
      parent_task_id: input.parentTaskId,
      created_at: now,
      updated_at: now,
    };

    await serenityClient.insertData('WorkflowTasks', task);
    console.log(`[Workflow] Created task ${taskId}: ${input.title}`);
    
    return task as WorkflowTask;
  }

  /**
   * Triggers automatic AI execution based on an event
   */
  async triggerAIWorkflow(
    event: keyof typeof WORKFLOW_TRIGGERS,
    caseId: string,
    debtorId: string,
    entityId: string,
    entityType: string
  ): Promise<{ task: WorkflowTask; aiResult: AIExecutionResult }> {
    const config = WORKFLOW_TRIGGERS[event];
    if (!config) {
      throw new Error(`Unknown workflow trigger: ${event}`);
    }

    // Create the task first (in pending state)
    const task = await this.createTask({
      caseId,
      debtorId,
      entityType,
      entityId,
      taskType: config.taskType,
      title: config.title,
      description: `Automatic ${config.title.toLowerCase()} triggered by ${event.replace(/_/g, ' ')}`,
      priority: this.getPriorityForEvent(event),
      aiAgentCode: config.agentCode,
    });

    // Execute the AI agent
    const aiResult = await this.executeAIAgent(config.agentCode, {
      caseId,
      debtorId,
      entityId,
    });

    // Update task with AI results
    await this.updateTaskWithAIResult(task.task_id, aiResult, config);

    return { task, aiResult };
  }

  /**
   * Executes an AI agent and returns structured results
   */
  private async executeAIAgent(
    agentCode: string,
    variables: Record<string, string | undefined>
  ): Promise<AIExecutionResult> {
    try {
      const cleanVars: Record<string, string> = {};
      Object.entries(variables).forEach(([k, v]) => {
        if (v) cleanVars[k] = v;
      });

      const result = await serenityClient.executeAgentWithContext(agentCode, cleanVars);
      
      // Try to parse the AI response (API returns 'content' not 'response')
      let recommendation = result.content;
      let confidence = 0.75;
      let parsed: Record<string, unknown> | undefined;

      try {
        parsed = JSON.parse(result.content);
        if (parsed) {
          recommendation = parsed.recommendation as string || 
                          parsed.ai_recommendation as string || 
                          parsed.action as string ||
                          result.content;
          confidence = (parsed.confidence as number) || 
                      (parsed.ai_confidence as number) || 
                      (parsed.confidence_score as number) || 
                      0.75;
        }
      } catch {
        // Response is not JSON, use as-is
      }

      return {
        success: true,
        recommendation,
        confidence,
        rawResult: parsed || result.content,
      };
    } catch (error) {
      console.error(`[Workflow] AI agent ${agentCode} failed:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        confidence: 0,
      };
    }
  }

  /**
   * Updates a task with AI execution results
   */
  private async updateTaskWithAIResult(
    taskId: string,
    aiResult: AIExecutionResult,
    config: { confidenceThreshold: number; requiresHumanReview: boolean }
  ): Promise<void> {
    const now = new Date().toISOString();
    
    let status: TaskStatus = 'pending';
    if (aiResult.success) {
      if (aiResult.confidence && aiResult.confidence >= config.confidenceThreshold && !config.requiresHumanReview) {
        status = 'completed';
      } else {
        status = 'awaiting_input';
      }
    }

    await serenityClient.updateDataWhere(
      'WorkflowTasks',
      {
        status,
        ai_recommendation: aiResult.recommendation,
        ai_confidence: aiResult.confidence,
        updated_at: now,
        ...(status === 'completed' ? { completed_at: now, completion_notes: 'Auto-completed by AI' } : {}),
      },
      `task_id = '${taskId}'`
    );

    console.log(`[Workflow] Updated task ${taskId} with AI result (confidence: ${aiResult.confidence?.toFixed(2)})`);
  }

  /**
   * Gets all pending tasks for a case
   */
  async getTasksForCase(caseId: string): Promise<WorkflowTask[]> {
    return serenityClient.queryData<WorkflowTask>(
      `SELECT * FROM WorkflowTasks WHERE case_id = '${caseId}' ORDER BY created_at DESC`
    );
  }

  /**
   * Gets all tasks assigned to an agent
   */
  async getTasksForAgent(agentId: string): Promise<WorkflowTask[]> {
    return serenityClient.queryData<WorkflowTask>(
      `SELECT * FROM WorkflowTasks 
       WHERE assigned_to = '${agentId}' AND status IN ('pending', 'in_progress', 'awaiting_input')
       ORDER BY priority DESC, due_date ASC`
    );
  }

  /**
   * Gets all unassigned tasks for a role
   */
  async getUnassignedTasksForRole(role: string): Promise<WorkflowTask[]> {
    return serenityClient.queryData<WorkflowTask>(
      `SELECT * FROM WorkflowTasks 
       WHERE assigned_to IS NULL AND assigned_role = '${role}' AND status = 'pending'
       ORDER BY priority DESC, due_date ASC`
    );
  }

  /**
   * Assigns a task to an agent
   */
  async assignTask(taskId: string, agentId: string): Promise<void> {
    await serenityClient.updateDataWhere(
      'WorkflowTasks',
      {
        assigned_to: agentId,
        updated_at: new Date().toISOString(),
      },
      `task_id = '${taskId}'`
    );
  }

  /**
   * Starts work on a task
   */
  async startTask(taskId: string): Promise<void> {
    await serenityClient.updateDataWhere(
      'WorkflowTasks',
      {
        status: 'in_progress',
        started_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      `task_id = '${taskId}'`
    );
  }

  /**
   * Completes a task
   */
  async completeTask(taskId: string, notes: string, approved?: boolean, approvedBy?: string): Promise<void> {
    const now = new Date().toISOString();
    const updates: Record<string, unknown> = {
      status: 'completed',
      completed_at: now,
      completion_notes: notes,
      updated_at: now,
    };

    if (approved !== undefined) {
      updates.approved_by = approvedBy;
      updates.approved_at = now;
    }

    await serenityClient.updateDataWhere('WorkflowTasks', updates, `task_id = '${taskId}'`);
  }

  /**
   * Escalates a task
   */
  async escalateTask(taskId: string, reason: string): Promise<void> {
    await serenityClient.updateDataWhere(
      'WorkflowTasks',
      {
        status: 'escalated',
        completion_notes: `Escalated: ${reason}`,
        updated_at: new Date().toISOString(),
      },
      `task_id = '${taskId}'`
    );
  }

  /**
   * Re-runs AI analysis for a task
   */
  async rerunAIAnalysis(taskId: string): Promise<AIExecutionResult> {
    const tasks = await serenityClient.queryData<WorkflowTask>(
      `SELECT * FROM WorkflowTasks WHERE task_id = '${taskId}' LIMIT 1`
    );
    
    if (!tasks[0]) throw new Error('Task not found');
    const task = tasks[0];
    
    if (!task.ai_agent_code) throw new Error('Task has no associated AI agent');

    const aiResult = await this.executeAIAgent(task.ai_agent_code, {
      caseId: task.case_id,
      debtorId: task.debtor_id,
      entityId: task.entity_id,
    });

    await serenityClient.updateDataWhere(
      'WorkflowTasks',
      {
        ai_recommendation: aiResult.recommendation,
        ai_confidence: aiResult.confidence,
        updated_at: new Date().toISOString(),
      },
      `task_id = '${taskId}'`
    );

    return aiResult;
  }

  // Helper methods
  private getWorkflowType(taskType: TaskType): string {
    const mapping: Record<string, string> = {
      document_review: 'document_processing',
      hardship_evaluation: 'hardship_review',
      payment_plan_validation: 'payment_monitoring',
      legal_assessment: 'lawsuit_preparation',
      segmentation: 'case_management',
      communication: 'communication',
    };
    return mapping[taskType] || 'general';
  }

  private getDefaultRole(taskType: TaskType): string {
    const mapping: Record<string, string> = {
      legal_assessment: 'legal_specialist',
      document_preparation: 'legal_specialist',
      court_filing: 'legal_specialist',
      approval_decision: 'supervisor',
      escalation_review: 'supervisor',
    };
    return mapping[taskType] || 'collections_agent';
  }

  private requiresApproval(taskType: TaskType): boolean {
    return ['approval_decision', 'legal_assessment', 'escalation_review', 'hardship_evaluation'].includes(taskType);
  }

  private calculateDueDate(priority: TaskPriority): string {
    const daysMap: Record<TaskPriority, number> = {
      urgent: 1,
      high: 3,
      normal: 7,
      low: 14,
    };
    const date = new Date();
    date.setDate(date.getDate() + daysMap[priority]);
    return date.toISOString();
  }

  private getPriorityForEvent(event: string): TaskPriority {
    const priorityMap: Record<string, TaskPriority> = {
      escalation_requested: 'high',
      hardship_submitted: 'high',
      document_uploaded: 'normal',
      payment_plan_created: 'normal',
      case_created: 'low',
    };
    return priorityMap[event] || 'normal';
  }
}

export const workflowService = new WorkflowService();

